import { useTranslation } from "react-i18next";
import { Button, Progress } from "antd";
import {
  PlayCircleFilled,
  CheckCircleFilled,
  ClockCircleOutlined,
  EnvironmentOutlined,
  VideoCameraFilled,
  ReadOutlined,
  FormOutlined,
  TrophyFilled,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  EducationType,
  EnrollmentStatus,
} from "../../types/client-education-detail.types";
import type {
  EducationDetailDto,
  EnrollmentDto,
  VirtualClassroomDto,
} from "../../types/client-education-detail.types";
import type { CtaDescriptor } from "../../utils/resolve-cta";
import {
  isRequestCollectionOpen,
  requestCollectionOpensAt,
} from "../../utils/request-collection";
import styles from "./ActiveStageCard.module.scss";

interface ActiveStageCardProps {
  education: EducationDetailDto;
  enrollment: EnrollmentDto | null;
  cta: CtaDescriptor | null;
  onCtaActivate: (action: CtaDescriptor["action"]) => void;
  onJump: (sectionId: string) => void;
}

interface Stage {
  icon: React.ReactNode;
  accent: "primary" | "success" | "warning" | "muted";
  kicker: string;
  title: string;
  description: string;
  primaryCtaLabel?: string;
  primaryCtaOnClick?: () => void;
  secondaryCtaLabel?: string;
  secondaryCtaOnClick?: () => void;
  showProgress?: boolean;
  progress?: number;
  countdown?: string | null;
}

function findNextSession(
  classrooms: VirtualClassroomDto[] | undefined,
  now: Date,
): { vc: VirtualClassroomDto; start: Date; isLiveWindow: boolean } | null {
  if (!classrooms?.length) return null;
  const events: { vc: VirtualClassroomDto; start: Date; end: Date }[] = [];
  for (const vc of classrooms) {
    const times = vc.scheduledTimes ?? [];
    if (times.length) {
      for (const t of times) {
        if (!t.date) continue;
        const start = dayjs(`${t.date}T${t.startTime ?? "00:00"}`);
        const end = dayjs(`${t.date}T${t.endTime ?? "23:59"}`);
        if (start.isValid() && end.isValid()) {
          events.push({ vc, start: start.toDate(), end: end.toDate() });
        }
      }
    } else if (vc.startDate) {
      const start = dayjs(vc.startDate);
      const end = vc.endDate ? dayjs(vc.endDate) : start.add(1, "hour");
      if (start.isValid()) {
        events.push({ vc, start: start.toDate(), end: end.toDate() });
      }
    }
  }
  events.sort((a, b) => a.start.getTime() - b.start.getTime());
  const nowMs = now.getTime();
  // Live window (şimdi içinde veya -15 dk öncesi)
  for (const ev of events) {
    const openStart = ev.start.getTime() - 15 * 60_000;
    const openEnd = ev.end.getTime();
    if (nowMs >= openStart && nowMs <= openEnd) {
      return { vc: ev.vc, start: ev.start, isLiveWindow: true };
    }
  }
  // Sonraki gelecek oturum
  for (const ev of events) {
    if (ev.start.getTime() > nowMs) {
      return { vc: ev.vc, start: ev.start, isLiveWindow: false };
    }
  }
  return null;
}

function formatCountdown(
  target: Date,
  now: Date,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return t("clientEducations.activeStage.now");
  const mins = Math.floor(diffMs / 60_000);
  const days = Math.floor(mins / (60 * 24));
  const hours = Math.floor((mins % (60 * 24)) / 60);
  const rem = mins % 60;
  if (days > 0) return t("clientEducations.activeStage.daysHours", { days, hours });
  if (hours > 0) return t("clientEducations.activeStage.hoursMinutes", { hours, rem });
  return t("clientEducations.activeStage.minutes", { rem });
}

function buildStage(
  education: EducationDetailDto,
  enrollment: EnrollmentDto | null,
  cta: CtaDescriptor | null,
  onCtaActivate: (action: CtaDescriptor["action"]) => void,
  onJump: (sectionId: string) => void,
  t: (k: string, opts?: Record<string, unknown>) => string,
): Stage {
  const now = new Date();

  // Kayıt yoksa: katalog modu — talep toplama durumuna göre uygun aksiyonu göster.
  if (!enrollment) {
    const rcOpen = isRequestCollectionOpen(education, now);
    const opensAt = rcOpen ? null : requestCollectionOpensAt(education, now);
    const rcStart = education.requestCollectionStartDate
      ? dayjs(education.requestCollectionStartDate)
      : null;
    const rcEnd = education.requestCollectionEndDate
      ? dayjs(education.requestCollectionEndDate)
      : null;
    const rcRange =
      rcStart?.isValid() && rcEnd?.isValid()
        ? `${rcStart.format("DD MMM YYYY")} – ${rcEnd.format("DD MMM YYYY")}`
        : rcEnd?.isValid()
          ? rcEnd.format("DD MMM YYYY")
          : null;

    if (rcOpen) {
      return {
        icon: <ReadOutlined />,
        accent: "primary",
        kicker: t("clientEducations.activeStage.rcOpenKicker"),
        title: t("clientEducations.activeStage.rcOpenTitle"),
        description: rcRange
          ? t("clientEducations.activeStage.rcOpenDescriptionRange", { range: rcRange })
          : t("clientEducations.activeStage.rcOpenDescription"),
        primaryCtaLabel: cta?.labelKey
          ? t(cta.labelKey)
          : t("clientEducations.activeStage.rcDefaultCta"),
        primaryCtaOnClick: cta
          ? () => onCtaActivate(cta.action)
          : undefined,
      };
    }

    if (opensAt) {
      return {
        icon: <ReadOutlined />,
        accent: "muted",
        kicker: t("clientEducations.activeStage.rcNotEnrolledKicker"),
        title: t("clientEducations.activeStage.rcUpcomingTitle"),
        description: rcEnd?.isValid()
          ? t("clientEducations.activeStage.rcUpcomingDescriptionRange", { from: dayjs(opensAt).format("DD MMM YYYY"), to: rcEnd.format("DD MMM YYYY") })
          : t("clientEducations.activeStage.rcUpcomingDescription", { date: dayjs(opensAt).format("DD MMM YYYY") }),
      };
    }

    return {
      icon: <ReadOutlined />,
      accent: "muted",
      kicker: t("clientEducations.activeStage.rcNotEnrolledKicker"),
      title: t("clientEducations.activeStage.rcCatalogTitle"),
      description: t("clientEducations.activeStage.rcCatalogDescription"),
    };
  }

  const st = enrollment.status;
  const progress = Math.round(enrollment.progressPercent ?? 0);

  // Tamamlandı
  if (st === EnrollmentStatus.COMPLETED) {
    return {
      icon: <TrophyFilled />,
      accent: "success",
      kicker: t("clientEducations.activeStage.completedKicker"),
      title: t("clientEducations.activeStage.completedTitle"),
      description: education.certificateId
        ? t("clientEducations.activeStage.completedDescriptionWithCert")
        : t("clientEducations.activeStage.completedDescription"),
      primaryCtaLabel: education.certificateId ? t("clientEducations.activeStage.completedShowCertificate") : undefined,
      primaryCtaOnClick: education.certificateId
        ? () => onJump("certificate")
        : undefined,
    };
  }

  // Sınav bekliyor
  if (st === EnrollmentStatus.CONTENT_COMPLETED && education.exam?.id) {
    return {
      icon: <FormOutlined />,
      accent: "warning",
      kicker: t("clientEducations.activeStage.examReadyKicker"),
      title: t("clientEducations.activeStage.examReadyTitle"),
      description: t("clientEducations.activeStage.examReadyDescription", { durationSuffix: education.exam.durationMinutes ? t("clientEducations.activeStage.examDurationSuffix", { minutes: education.exam.durationMinutes }) : "." }),
      primaryCtaLabel: cta?.labelKey
        ? t(cta.labelKey)
        : t("clientEducations.activeStage.examReadyCta"),
      primaryCtaOnClick: cta ? () => onCtaActivate(cta.action) : undefined,
    };
  }

  if (st === EnrollmentStatus.EXAM_STARTED) {
    return {
      icon: <FormOutlined />,
      accent: "warning",
      kicker: t("clientEducations.activeStage.examInProgressKicker"),
      title: t("clientEducations.activeStage.examInProgressTitle"),
      description: t("clientEducations.activeStage.examInProgressDescription"),
      primaryCtaLabel: t("clientEducations.activeStage.examInProgressCta"),
      primaryCtaOnClick: cta ? () => onCtaActivate(cta.action) : undefined,
    };
  }

  if (st === EnrollmentStatus.EXAM_FAILED) {
    return {
      icon: <FormOutlined />,
      accent: "warning",
      kicker: t("clientEducations.activeStage.examFailedKicker"),
      title: t("clientEducations.activeStage.examFailedTitle"),
      description: t("clientEducations.activeStage.examFailedDescription"),
      primaryCtaLabel: t("clientEducations.activeStage.examFailedCta"),
      primaryCtaOnClick: cta ? () => onCtaActivate(cta.action) : undefined,
    };
  }

  if (
    st === EnrollmentStatus.EXAM_COMPLETED &&
    education.survey &&
    !enrollment.surveyCompletedAt
  ) {
    return {
      icon: <FormOutlined />,
      accent: "primary",
      kicker: t("clientEducations.activeStage.surveyPendingKicker"),
      title: t("clientEducations.activeStage.surveyPendingTitle"),
      description: t("clientEducations.activeStage.surveyPendingDescription"),
      primaryCtaLabel: t("clientEducations.activeStage.surveyPendingCta"),
      primaryCtaOnClick: cta ? () => onCtaActivate(cta.action) : undefined,
    };
  }

  if (st === EnrollmentStatus.SURVEY_STARTED) {
    return {
      icon: <FormOutlined />,
      accent: "primary",
      kicker: t("clientEducations.activeStage.surveyInProgressKicker"),
      title: t("clientEducations.activeStage.surveyInProgressTitle"),
      description: t("clientEducations.activeStage.surveyInProgressDescription"),
      primaryCtaLabel: t("clientEducations.activeStage.surveyInProgressCta"),
      primaryCtaOnClick: cta ? () => onCtaActivate(cta.action) : undefined,
    };
  }

  // ENROLLED — tür bazlı ana aşama
  if (st === EnrollmentStatus.ENROLLED) {
    if (education.type === EducationType.ONLINE) {
      const next = findNextSession(education.virtualClassrooms, now);
      if (next?.isLiveWindow) {
        return {
          icon: <VideoCameraFilled />,
          accent: "warning",
          kicker: t("clientEducations.activeStage.liveNowKicker"),
          title: t("clientEducations.activeStage.liveNowTitle", { name: next.vc.name ?? t("clientEducations.activeStage.liveNowFallbackName") }),
          description: t("clientEducations.activeStage.liveNowDescription"),
          primaryCtaLabel: t("clientEducations.activeStage.liveNowCta"),
          primaryCtaOnClick: cta
            ? () => onCtaActivate(cta.action)
            : undefined,
          showProgress: false,
        };
      }
      if (next) {
        return {
          icon: <VideoCameraFilled />,
          accent: "primary",
          kicker: t("clientEducations.activeStage.nextLiveKicker"),
          title: next.vc.name ?? t("clientEducations.activeStage.nextLiveFallbackName"),
          description: t("clientEducations.activeStage.nextLiveDescription", { datetime: dayjs(next.start).format("DD MMM YYYY · HH:mm") }),
          countdown: formatCountdown(next.start, now, t),
          primaryCtaLabel: t("clientEducations.activeStage.viewSessions"),
          primaryCtaOnClick: () => onJump("virtualClassrooms"),
        };
      }
      return {
        icon: <VideoCameraFilled />,
        accent: "muted",
        kicker: t("clientEducations.activeStage.noLiveScheduleKicker"),
        title: t("clientEducations.activeStage.noLiveScheduleTitle"),
        description: t("clientEducations.activeStage.noLiveScheduleDescription"),
      };
    }

    if (education.type === EducationType.ON_SITE) {
      const next = findNextSession(education.virtualClassrooms, now);
      const todayStr = dayjs(now).format("YYYY-MM-DD");
      const hasToday = (education.scheduledTimes ?? []).some(
        (t) => t.date === todayStr,
      );
      if (hasToday) {
        return {
          icon: <EnvironmentOutlined />,
          accent: "warning",
          kicker: t("clientEducations.activeStage.todaysClassKicker"),
          title: t("clientEducations.activeStage.todaysClassTitle"),
          description: education.location
            ? t("clientEducations.activeStage.todaysClassLocation", { location: education.location })
            : t("clientEducations.activeStage.todaysClassDescription"),
          primaryCtaLabel: t("clientEducations.activeStage.todaysClassCta"),
          primaryCtaOnClick: () => onJump("scheduling"),
        };
      }
      if (next) {
        return {
          icon: <EnvironmentOutlined />,
          accent: "primary",
          kicker: t("clientEducations.activeStage.nextClassroomKicker"),
          title: next.vc.name ?? t("clientEducations.activeStage.nextClassroomFallbackName"),
          description: t("clientEducations.activeStage.nextClassroomDescription", { datetime: dayjs(next.start).format("DD MMM YYYY · HH:mm"), location: next.vc.location ?? education.location ?? t("clientEducations.activeStage.nextClassroomLocationFallback") }),
          countdown: formatCountdown(next.start, now, t),
          primaryCtaLabel: t("clientEducations.activeStage.nextClassroomCta"),
          primaryCtaOnClick: () => onJump("scheduling"),
        };
      }
      return {
        icon: <EnvironmentOutlined />,
        accent: "muted",
        kicker: t("clientEducations.activeStage.noClassroomKicker"),
        title: t("clientEducations.activeStage.noClassroomTitle"),
        description: t("clientEducations.activeStage.noClassroomDescription"),
      };
    }

    if (
      education.type === EducationType.UNIT ||
      education.type === EducationType.NONUNIT
    ) {
      return {
        icon: <PlayCircleFilled />,
        accent: "primary",
        kicker: progress > 0 ? t("clientEducations.activeStage.unitContinueKicker") : t("clientEducations.activeStage.unitStartKicker"),
        title: progress > 0 ? t("clientEducations.activeStage.unitProgressTitle", { progress }) : t("clientEducations.activeStage.unitStartTitle"),
        description: education.type === EducationType.UNIT
            ? t("clientEducations.activeStage.unitDescription")
            : t("clientEducations.activeStage.nonUnitDescription"),
        primaryCtaLabel: progress > 0 ? t("clientEducations.activeStage.unitContinueCta") : t("clientEducations.activeStage.unitStartCta"),
        // continueContent action'ı kullan: hem scroll yapıyor hem ContentSection'da
        // ilk yarım kalan/başlamamış objective'i otomatik seçiyor.
        primaryCtaOnClick: () =>
          onCtaActivate({ kind: "continueContent" }),
        showProgress: true,
        progress,
      };
    }
  }

  // Fallback
  return {
    icon: <ClockCircleOutlined />,
    accent: "muted",
    kicker: t("clientEducations.activeStage.fallbackKicker"),
    title: education.name,
    description: t("clientEducations.activeStage.fallbackDescription"),
  };
}

export function ActiveStageCard({
  education,
  enrollment,
  cta,
  onCtaActivate,
  onJump,
}: ActiveStageCardProps) {
  const { t } = useTranslation();
  const stage = buildStage(education, enrollment, cta, onCtaActivate, onJump, t);

  return (
    <section
      className={`${styles.card} ${styles[`accent_${stage.accent}`]}`}
      aria-label={t("clientEducations.activeStage.kicker")}
    >
      <div className={styles.iconCircle}>{stage.icon}</div>
      <div className={styles.body}>
        <span className={styles.kicker}>{stage.kicker}</span>
        <h2 className={styles.title}>{stage.title}</h2>
        <p className={styles.description}>{stage.description}</p>

        {stage.showProgress && (
          <div className={styles.progressWrap}>
            <Progress
              percent={stage.progress ?? 0}
              showInfo={false}
              strokeLinecap="round"
            />
            <span className={styles.progressLabel}>
              {stage.progress ?? 0}%
            </span>
          </div>
        )}

        {stage.countdown && (
          <div className={styles.countdown}>
            <ClockCircleOutlined /> {t("clientEducations.activeStage.countdownPrefix")} {stage.countdown}
          </div>
        )}

        {(stage.primaryCtaLabel || stage.secondaryCtaLabel) && (
          <div className={styles.actions}>
            {stage.primaryCtaLabel && (
              <Button
                type="primary"
                size="large"
                onClick={stage.primaryCtaOnClick}
                disabled={!stage.primaryCtaOnClick}
              >
                {stage.primaryCtaLabel}
              </Button>
            )}
            {stage.secondaryCtaLabel && (
              <Button size="large" onClick={stage.secondaryCtaOnClick}>
                {stage.secondaryCtaLabel}
              </Button>
            )}
          </div>
        )}
      </div>

      {enrollment && (
        <div className={styles.statusBadge}>
          <CheckCircleFilled /> {t("clientEducations.activeStage.enrolled")}
        </div>
      )}
    </section>
  );
}
